import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TelegramWidgetPayload } from "../../services/auth";
import { isKredafiShell } from "../../utils/brand";

/**
 * Кнопка «Войти через Telegram» на основе официального Login Widget.
 *
 * Виджет — сторонний скрипт, который сам рисует кнопку в переданный контейнер
 * и вызывает глобальную функцию с подписанными данными. Отсюда две особенности:
 * имя коллбэка приходится класть в window, а неудачу загрузки скрипта нужно
 * показывать человеку — иначе он видит пустое место и не понимает, что делать.
 *
 * Домен сайта должен быть привязан к боту через BotFather (/setdomain),
 * иначе Telegram откажется рисовать виджет.
 */

const CALLBACK_NAME = "onWebtransferTelegramAuth";

interface Props {
  onAuth: (payload: TelegramWidgetPayload) => void;
  onUnavailable?: () => void;
  disabled?: boolean;
  /** Подпись у виджета своя; при mode=link кнопка обычно уже, чем на входе. */
  size?: "large" | "medium" | "small";
}

const TelegramLoginButton: React.FC<Props> = ({
  onAuth,
  onUnavailable,
  disabled = false,
  size = "large",
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  const isKredafi =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("kredafi") || isKredafiShell());

  const botUsername = isKredafi
    ? "KredaFiBot"
    : (process.env.REACT_APP_TELEGRAM_BOT_USERNAME || "Webtransfer_apps_bot");

  // Для webtransfer.app, kredafi.webtransfer.app и kredafi.com боты привязаны напрямую через BotFather (/setdomain).
  // На остальных субдоменах бот может требовать единый SSO-шлюз.
  const isSubdomain =
    typeof window !== "undefined" &&
    window.location.hostname !== "webtransfer.app" &&
    window.location.hostname !== "kredafi.webtransfer.app" &&
    window.location.hostname !== "kredafi.com" &&
    window.location.hostname !== "www.kredafi.com" &&
    window.location.hostname !== "localhost";

  const handleSsoClick = () => {
    const returnTo = encodeURIComponent(window.location.origin);
    const w = 480;
    const h = 600;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      `/api/auth/sso.php?provider=telegram&return_to=${returnTo}`,
      "kredafi_telegram_auth",
      `width=${w},height=${h},left=${left},top=${top},status=no,toolbar=no,menubar=no,location=no`
    );
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      // Блокировщик всплывающих окон — плавный редирект
      window.location.href = `/api/auth/sso.php?provider=telegram&return_to=${encodeURIComponent(window.location.href)}`;
    }
  };

  useEffect(() => {
    if (isSubdomain) {
      return;
    }

    const container = containerRef.current;
    if (!container || disabled) {
      return;
    }

    (window as any)[CALLBACK_NAME] = (user: TelegramWidgetPayload) => {
      onAuth(user);
    };

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", size);
    script.setAttribute("data-radius", "12");
    script.setAttribute("data-request-access", "write");
    script.setAttribute("data-onauth", `${CALLBACK_NAME}(user)`);

    script.onerror = () => {
      setFailed(true);
      onUnavailable?.();
    };

    container.appendChild(script);

    return () => {
      container.innerHTML = "";
      delete (window as any)[CALLBACK_NAME];
    };
  }, [onAuth, onUnavailable, disabled, size, isSubdomain, botUsername]);

  if (isSubdomain || failed) {
    return (
      <button
        type="button"
        className="auth-sso-telegram-btn"
        onClick={handleSsoClick}
        disabled={disabled}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
        </svg>
        <span>{t("Sign in with Telegram")}</span>
      </button>
    );
  }

  return <div ref={containerRef} className="auth-telegram-widget" />;
};

export default TelegramLoginButton;
